import React, { useState, useEffect } from 'react';
import { authFetch } from './authFetch';
import { format, parseISO } from 'date-fns';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function PolicyTab({ customerId }) {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPolicy, setExpandedPolicy] = useState(null);

  useEffect(() => {
    fetchPolicyData();
  }, [customerId]);

  const fetchPolicyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authFetch(`${BASE_URL}/api/customers/${customerId}/policy/`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch policy data: ${response.status}`);
      }
      
      const data = await response.json();
      setPolicyData(data);
    } catch (err) {
      console.error('Error fetching policy data:', err);
      setError(err.message || 'Failed to load policy information');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle MM/DD/YYYY format from Florida Blue data
      if (dateString.includes('/')) {
        const [month, day, year] = dateString.split('/');
        const date = new Date(year, month - 1, day);
        return format(date, 'MM/dd/yyyy');
      }
      // Handle ISO format
      return format(parseISO(dateString), 'MM/dd/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    
    // If it's already a formatted string like "$ 1,088.03"
    if (typeof amount === 'string' && amount.includes('$')) {
      return amount;
    }
    
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  // ENHANCED: Updated to properly handle cancelled statuses with debugging
  const getStatusBadge = (policy) => {
    const recordType = policy.record_type || '';
    const contractStatus = (policy.contract_status || '').toUpperCase();
    const displayStatus = policy.display_status || contractStatus;
    
    let badgeClass = 'px-2 py-1 rounded-full text-xs font-medium';
    
    // Check contract status first for most accurate determination
    if (['ACTIVE', 'ACTV'].includes(contractStatus)) {
      badgeClass += ' bg-green-100 text-green-800';
    } else if (['CNCL', 'CANCELLED', 'TERMINATED', 'CANCEL', 'TERM'].includes(contractStatus)) {
      badgeClass += ' bg-red-100 text-red-800';
    } else if (['SUSPENDED', 'SUSP'].includes(contractStatus)) {
      badgeClass += ' bg-orange-100 text-orange-800';
    } else if (['PENDING', 'PEND'].includes(contractStatus)) {
      badgeClass += ' bg-yellow-100 text-yellow-800';
    } else {
      // Fallback to record type-based logic
      if (recordType === 'POLICY' || recordType === 'DIRECT_POLICY' || recordType === 'ORPHANED_POLICY') {
        if (displayStatus.toLowerCase().includes('active')) {
          badgeClass += ' bg-green-100 text-green-800';
        } else if (displayStatus.toLowerCase().includes('cancelled') || 
                   displayStatus.toLowerCase().includes('terminated')) {
          badgeClass += ' bg-red-100 text-red-800';
        } else {
          badgeClass += ' bg-blue-100 text-blue-800';
        }
      } else if (recordType === 'PENDING_APPLICATION') {
        badgeClass += ' bg-yellow-100 text-yellow-800';
      } else if (recordType === 'FAILED_APPLICATION') {
        badgeClass += ' bg-red-100 text-red-800';
      } else {
        badgeClass += ' bg-gray-100 text-gray-800';
      }
    }
    
    return (
      <span className={badgeClass}>
        {displayStatus || 'Unknown'}
      </span>
    );
  };

  const getRecordTypeIcon = (recordType) => {
    switch (recordType) {
      case 'POLICY':
        return '📝'; // Active policy
      case 'PENDING_APPLICATION':
        return '⏳'; // Pending application
      case 'FAILED_APPLICATION':
        return '❌'; // Failed application
      case 'DIRECT_POLICY':
        return '📋'; // Direct policy
      case 'ORPHANED_POLICY':
        return '🔗'; // Orphaned policy (recovered from import)
      default:
        return '📄'; // Default document icon
    }
  };

  const togglePolicyExpanded = (policyId) => {
    setExpandedPolicy(expandedPolicy === policyId ? null : policyId);
  };

  // ENHANCED: Helper functions to determine if a policy is actually active with debugging and raw data fallback
  const isActivePolicy = (policy) => {
    const contractStatus = (policy.contract_status || '').toString().toUpperCase().trim();
    const displayStatus = (policy.display_status || '').toString().toLowerCase().trim();
    
    // ENHANCED: Also check raw_data as fallback
    const rawContractStatus = policy.raw_data && policy.raw_data['Contract Status'] 
      ? policy.raw_data['Contract Status'].toString().toUpperCase().trim() 
      : '';
    
    console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - isActive check:`, {
      contract_status: contractStatus,
      raw_Contract_Status: rawContractStatus,
      display_status: displayStatus,
      record_type: policy.record_type
    });
    
    // Check contract status first (most reliable)
    if (['ACTIVE', 'ACTV'].includes(contractStatus)) {
      console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - ACTIVE via contract_status`);
      return true;
    }
    
    // ENHANCED: Fallback to raw data if contract_status is empty
    if (!contractStatus && ['ACTIVE', 'ACTV'].includes(rawContractStatus)) {
      console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - ACTIVE via raw Contract Status`);
      return true;
    }
    
    // Check for cancelled/terminated statuses
    if (['CNCL', 'CANCELLED', 'TERMINATED', 'CANCEL', 'TERM', 'SUSPENDED', 'SUSP'].includes(contractStatus)) {
      console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - NOT ACTIVE via contract_status (${contractStatus})`);
      return false;
    }
    
    // ENHANCED: Also check raw data for cancelled statuses
    if (['CNCL', 'CANCELLED', 'TERMINATED', 'CANCEL', 'TERM', 'SUSPENDED', 'SUSP'].includes(rawContractStatus)) {
      console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - NOT ACTIVE via raw Contract Status (${rawContractStatus})`);
      return false;
    }
    
    // Fallback to display status if contract status is unclear
    const isActiveByDisplay = displayStatus.includes('active') && !displayStatus.includes('cancelled') && !displayStatus.includes('terminated');
    console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - Fallback to display status: ${isActiveByDisplay}`);
    return isActiveByDisplay;
  };

  const isCancelledPolicy = (policy) => {
    const contractStatus = (policy.contract_status || '').toString().toUpperCase().trim();
    const displayStatus = (policy.display_status || '').toString().toLowerCase().trim();
    
    // ENHANCED: Also check raw_data as fallback
    const rawContractStatus = policy.raw_data && policy.raw_data['Contract Status'] 
      ? policy.raw_data['Contract Status'].toString().toUpperCase().trim() 
      : '';
    
    console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - isCancelled check:`, {
      contract_status: contractStatus,
      raw_Contract_Status: rawContractStatus,
      display_status: displayStatus
    });
    
    // Check contract status
    if (['CNCL', 'CANCELLED', 'TERMINATED', 'CANCEL', 'TERM', 'SUSPENDED', 'SUSP'].includes(contractStatus)) {
      console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - CANCELLED via contract_status`);
      return true;
    }
    
    // ENHANCED: Fallback to raw data
    if (['CNCL', 'CANCELLED', 'TERMINATED', 'CANCEL', 'TERM', 'SUSPENDED', 'SUSP'].includes(rawContractStatus)) {
      console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - CANCELLED via raw Contract Status`);
      return true;
    }
    
    // Check display status
    const isCancelledByDisplay = displayStatus.includes('cancelled') || displayStatus.includes('terminated') || displayStatus.includes('suspended');
    console.log(`DEBUG: Policy ${policy.fb_uid || policy.id} - Cancelled by display: ${isCancelledByDisplay}`);
    return isCancelledByDisplay;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading policy information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Policy Data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button 
          onClick={fetchPolicyData}
          className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!policyData) {
    return (
      <div className="text-center p-8 text-gray-500">
        No policy data available
      </div>
    );
  }

  const { 
    customer = {}, 
    summary, 
    policies = [], 
    business_logic_applied 
  } = policyData || {};  
  
  // ENHANCED: Separate policies by type for better organization using proper business logic with debugging
  console.log('DEBUG: All policies received:', policies.length);
  policies.forEach(p => {
    console.log(`DEBUG: Policy ${p.fb_uid || p.id} - Type: ${p.record_type}, Contract Status: ${p.contract_status}, Display Status: ${p.display_status}`);
  });
  
  const activePolicies = policies.filter(p => {
    const isPolicy = p.record_type === 'POLICY' || p.record_type === 'DIRECT_POLICY' || p.record_type === 'ORPHANED_POLICY';
    const isActive = isActivePolicy(p);
    const result = isPolicy && isActive;
    console.log(`DEBUG: Policy ${p.fb_uid || p.id} - Active filter: isPolicy=${isPolicy}, isActive=${isActive}, result=${result}`);
    return result;
  });
  
  const cancelledPolicies = policies.filter(p => {
    const isPolicy = p.record_type === 'POLICY' || p.record_type === 'DIRECT_POLICY' || p.record_type === 'ORPHANED_POLICY';
    const isCancelled = isCancelledPolicy(p);
    const result = isPolicy && isCancelled;
    console.log(`DEBUG: Policy ${p.fb_uid || p.id} - Cancelled filter: isPolicy=${isPolicy}, isCancelled=${isCancelled}, result=${result}`);
    return result;
  });
  
  const pendingApplications = policies.filter(p => p.record_type === 'PENDING_APPLICATION');
  const failedApplications = policies.filter(p => p.record_type === 'FAILED_APPLICATION');
  const otherPolicies = policies.filter(p => 
    !activePolicies.includes(p) && 
    !cancelledPolicies.includes(p) && 
    !pendingApplications.includes(p) && 
    !failedApplications.includes(p)
  );

  console.log('DEBUG: Policy filtering results:', {
    total: policies.length,
    active: activePolicies.length,
    cancelled: cancelledPolicies.length,
    pending: pendingApplications.length,
    failed: failedApplications.length,
    other: otherPolicies.length
  });

  return (
    <div className="space-y-6">
      {/* Policy Overview */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Policy Overview</h3>
          
          {/* FIXED: Updated summary statistics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{activePolicies.length}</div>
              <div className="text-sm text-green-600">Active Policies</div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{cancelledPolicies.length}</div>
              <div className="text-sm text-red-600">Cancelled Policies</div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{policies.length}</div>
              <div className="text-sm text-blue-600">Total Records</div>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{pendingApplications.length}</div>
              <div className="text-sm text-yellow-600">Pending Applications</div>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{failedApplications.length}</div>
              <div className="text-sm text-orange-600">Failed Applications</div>
            </div>
          </div>
          
          {customer.fb_uid && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm font-medium text-blue-900">Florida Blue Member ID</div>
              <div className="text-sm text-blue-700 font-mono">{customer.fb_uid}</div>
            </div>
          )}
          
          {/* Business Logic Debug Info (Admin Only) */}
          {business_logic_applied && (
            <details className="mt-4">
              <summary className="text-sm text-gray-500 cursor-pointer">Business Logic Applied (Debug Info)</summary>
              <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                <p>App Tracker Records: {business_logic_applied.app_tracker_records_processed}</p>
                <p>Aligned Account Records: {business_logic_applied.aligned_account_records_processed}</p>
                <p>Successful Policy Matches: {business_logic_applied.successful_policy_matches}</p>
                <p>Unmatched Applications: {business_logic_applied.unmatched_applications}</p>
              </div>
            </details>
          )}
        </div>
      </div>

      {/* Active Policies */}
      {activePolicies.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Active Policies</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {activePolicies.map((policy) => (
              <PolicyRecord 
                key={policy.id} 
                policy={policy} 
                expanded={expandedPolicy === policy.id}
                onToggleExpanded={() => togglePolicyExpanded(policy.id)}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
                getRecordTypeIcon={getRecordTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* ENHANCED: Added Cancelled/Terminated Policies Section */}
      {cancelledPolicies.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Cancelled/Terminated Policies</h3>
            <p className="text-sm text-gray-600 mt-1">
              Policies that were previously active but have been cancelled or terminated
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {cancelledPolicies.map((policy) => (
              <PolicyRecord 
                key={policy.id} 
                policy={policy} 
                expanded={expandedPolicy === policy.id}
                onToggleExpanded={() => togglePolicyExpanded(policy.id)}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
                getRecordTypeIcon={getRecordTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pending Applications */}
      {pendingApplications.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Pending Applications</h3>
            <p className="text-sm text-gray-600 mt-1">
              Applications submitted but not yet converted to policies
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {pendingApplications.map((policy) => (
              <PolicyRecord 
                key={policy.id} 
                policy={policy} 
                expanded={expandedPolicy === policy.id}
                onToggleExpanded={() => togglePolicyExpanded(policy.id)}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
                getRecordTypeIcon={getRecordTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Failed Applications */}
      {failedApplications.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Failed Applications</h3>
            <p className="text-sm text-gray-600 mt-1">
              Applications that were not approved or processed
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {failedApplications.map((policy) => (
              <PolicyRecord 
                key={policy.id} 
                policy={policy} 
                expanded={expandedPolicy === policy.id}
                onToggleExpanded={() => togglePolicyExpanded(policy.id)}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
                getRecordTypeIcon={getRecordTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other Policy Records */}
      {otherPolicies.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Policy History</h3>
            <p className="text-sm text-gray-600 mt-1">
              Historical policy records and other entries
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {otherPolicies.map((policy) => (
              <PolicyRecord 
                key={policy.id} 
                policy={policy} 
                expanded={expandedPolicy === policy.id}
                onToggleExpanded={() => togglePolicyExpanded(policy.id)}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
                getRecordTypeIcon={getRecordTypeIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Policies Message */}
      {policies.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center">
          <div className="text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Policy Records Found</h3>
            <p className="text-gray-600">
              This customer doesn't have any policy records or applications on file.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Separate component for individual policy records
function PolicyRecord({ 
  policy, 
  expanded, 
  onToggleExpanded, 
  formatDate, 
  formatCurrency, 
  getStatusBadge, 
  getRecordTypeIcon 
}) {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <span className="text-lg">{getRecordTypeIcon(policy.record_type)}</span>
            <h4 className="font-medium text-gray-900">
              {policy.plan_name || 'Unknown Plan'}
            </h4>
            {getStatusBadge(policy)}
            {policy.metal_level && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {policy.metal_level}
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Contract ID:</span>
              <div className="text-gray-600 font-mono text-xs">
                {policy.contract_id || 'N/A'}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Effective:</span>
              <div className="text-gray-600">{formatDate(policy.effective_date)}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Premium:</span>
              <div className="text-gray-600">{formatCurrency(policy.premium)}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Source:</span>
              <div className="text-gray-600">{policy.import_source || 'N/A'}</div>
            </div>
          </div>

          {/* Application Context (if this policy came from an application) */}
          {policy.application_context && (
            <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
              <span className="font-medium text-blue-800">Application History:</span>
              <span className="text-blue-700 ml-1">
                Applied {formatDate(policy.application_context.original_application_date)} 
                (Status: {policy.application_context.original_application_status})
                {policy.application_context.application_id && (
                  <span> - ID: {policy.application_context.application_id}</span>
                )}
              </span>
            </div>
          )}

          {/* Business-specific indicators */}
          {policy.record_type === 'PENDING_APPLICATION' && (
            <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-800">
              ⏳ This application is still being processed and has not yet become an active policy.
            </div>
          )}
          
          {policy.record_type === 'FAILED_APPLICATION' && (
            <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-800">
              ❌ This application was not approved or was withdrawn.
            </div>
          )}

          {/* ENHANCED: Added indication for cancelled policies with raw data fallback */}
          {((policy.contract_status && ['CNCL', 'CANCELLED', 'TERMINATED'].includes(policy.contract_status.toUpperCase())) ||
            (policy.raw_data && policy.raw_data['Contract Status'] && ['CNCL', 'CANCELLED', 'TERMINATED'].includes(policy.raw_data['Contract Status'].toUpperCase()))) && (
            <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-800">
              🚫 This policy has been cancelled or terminated.
              {policy.raw_data && policy.raw_data['Cancellation Reason'] && (
                <span className="ml-1">Reason: {policy.raw_data['Cancellation Reason']}</span>
              )}
              {policy.raw_data && policy.raw_data['Coverage Expiration Date'] && (
                <span className="ml-1">Expired: {policy.raw_data['Coverage Expiration Date']}</span>
              )}
            </div>
          )}
        </div>
        
        <button
          onClick={onToggleExpanded}
          className="ml-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          {expanded ? 'Hide Details' : 'Show Details'}
        </button>
      </div>
      
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
            <div>
              <span className="font-medium text-gray-700">FB UID:</span>
              <div className="text-gray-600 font-mono text-xs">
                {policy.fb_uid || 'N/A'}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Import Date:</span>
              <div className="text-gray-600">{formatDate(policy.imported_at)}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Agent:</span>
              <div className="text-gray-600">{policy.agent || 'N/A'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Record Type:</span>
              <div className="text-gray-600">{policy.record_type || 'N/A'}</div>
            </div>
          </div>
          
          {/* ENHANCED: Show contract status and cancellation details for cancelled policies with raw data fallback */}
          {(policy.contract_status || (policy.raw_data && policy.raw_data['Contract Status'])) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <span className="font-medium text-gray-700">Contract Status:</span>
                <div className={`text-sm font-medium ${
                  ['CNCL', 'CANCELLED', 'TERMINATED'].includes((policy.contract_status || policy.raw_data?.['Contract Status'] || '').toUpperCase()) 
                    ? 'text-red-600' 
                    : ['ACTIVE', 'ACTV'].includes((policy.contract_status || policy.raw_data?.['Contract Status'] || '').toUpperCase())
                    ? 'text-green-600'
                    : 'text-gray-600'
                }`}>
                  {policy.contract_status || policy.raw_data?.['Contract Status'] || 'N/A'}
                </div>
              </div>
              {policy.raw_data && policy.raw_data['Coverage Expiration Date'] && (
                <div>
                  <span className="font-medium text-gray-700">Coverage Expiration:</span>
                  <div className="text-gray-600">{policy.raw_data['Coverage Expiration Date']}</div>
                </div>
              )}
            </div>
          )}
          
          {/* Additional Florida Blue specific fields */}
          {(policy.member_responsibility || policy.aptc_amount || policy.is_delinquent) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
              {policy.member_responsibility && (
                <div>
                  <span className="font-medium text-gray-700">Member Responsibility:</span>
                  <div className="text-gray-600">{formatCurrency(policy.member_responsibility)}</div>
                </div>
              )}
              {policy.aptc_amount && (
                <div>
                  <span className="font-medium text-gray-700">APTC Amount:</span>
                  <div className="text-gray-600">{formatCurrency(policy.aptc_amount)}</div>
                </div>
              )}
              {policy.is_delinquent && (
                <div>
                  <span className="font-medium text-gray-700">Delinquent:</span>
                  <div className={`text-sm ${policy.is_delinquent === 'YES' ? 'text-red-600' : 'text-green-600'}`}>
                    {policy.is_delinquent}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Payment Information */}
          {(policy.binder_payment_received || policy.payment_status) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
              {policy.binder_payment_received && (
                <div>
                  <span className="font-medium text-gray-700">Binder Payment:</span>
                  <div className="text-gray-600">{policy.binder_payment_received}</div>
                </div>
              )}
              {policy.payment_status && (
                <div>
                  <span className="font-medium text-gray-700">Payment Status:</span>
                  <div className="text-gray-600">{policy.payment_status}</div>
                </div>
              )}
            </div>
          )}
          
          {/* Application Information */}
          {(policy.application_date || policy.application_status) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
              {policy.application_date && (
                <div>
                  <span className="font-medium text-gray-700">Application Date:</span>
                  <div className="text-gray-600">{formatDate(policy.application_date)}</div>
                </div>
              )}
              {policy.application_status && (
                <div>
                  <span className="font-medium text-gray-700">Application Status:</span>
                  <div className="text-gray-600">{policy.application_status}</div>
                </div>
              )}
            </div>
          )}
          
          {policy.raw_data && Object.keys(policy.raw_data).length > 0 && (
            <div className="mt-4">
              <span className="font-medium text-gray-700">Raw Data:</span>
              <pre className="mt-1 bg-gray-50 p-3 rounded text-xs overflow-x-auto max-h-40">
                {JSON.stringify(policy.raw_data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PolicyTab;